// @ts-strict-ignore
import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProjectVO } from '@project/model/vo/project.vo';
import { ArticleDataService } from '@article/service/article-data.service';
import { ProjectModel } from '@project/model/project.model';
import { ItemListObjectVO } from '@article/service/model/item-list-object.vo';
import { DateFormatter } from '@shared/date-formatter';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleNumberMappingVO } from '@article/model/vo/article-number-mapping.vo';
import { ArticleNumberMappingUtils } from '@article/util/article-number-mapping-utils';
import { UserModel } from '@user/model/user.model';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { CSVUtils } from '@article/components/custom-article-number/edit-article-number-mapping/csv-utils';
import { FileUtils } from '@shared/util/file-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectUtils } from '@project/components/project/util/project-utils';

interface CompareListElement {
  articleNumber: string;
  amountA: number;
  amountB: number;
}

@Component({
  standalone: false,
  selector: 'app-compare-item-lists',
  templateUrl: './compare-item-lists.component.html',
  styleUrls: ['./compare-item-lists.component.scss'],
})
export class CompareItemListsComponent implements OnInit {

  public projectNameStringA: string = '';
  public projectNameStringB: string = '';
  public projectPathStringA: string = '';
  @Input() public projectPathStringB: string = '';
  public comparedList: CompareListElement[] = [];
  public statusMessage: string = '';

  public projectA: ProjectVO;
  @Input() public projectB: ProjectVO;
  private itemListA: ItemListObjectVO[] = [];
  private itemListB: ItemListObjectVO[] = [];

  constructor(
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private articleDataService: ArticleDataService,
    private projectContentPresenter: ProjectContentPresenter,
    private localeResourceManager: LocaleResourceManager
  ) {}

  ngOnInit(): void {
    this.projectA = this.projectModel.currentProject;
    this.projectNameStringA = this.buildProjectLabel(this.projectA);
    this.projectPathStringA = ProjectUtils.getPathNameForProjectGroup(
      this.localeResourceManager,
      this.projectA.projectGroup,
      this.userModel ? this.userModel.user : null,
      false
    );
    this.projectNameStringB = this.buildProjectLabel(this.projectB);
    this.loadFirstList();
  }

  private buildProjectLabel(project: ProjectVO): string {
    if (!project) {
      return '';
    }
    let result: string = project.name;
    if (project.modificationDate || project.currentVersionDisplayString) {
      result += ' (';
      if (project.currentVersionDisplayString) {
        result += project.currentVersionDisplayString + ' - ';
      }
      if (project.modificationDate) {
        result += DateFormatter.formatDate(project.modificationDate);
      }
      result += ')';
    }
    return result;
  }

  private loadFirstList(): void {
    this.statusMessage = 'compareItemListsComponent.state.readingProjectA';
    this.projectModel.currentProject.representations = [];
    this.articleDataService.getItemListByProject(this.projectModel.currentProject).subscribe(
      (result) => {
        this.itemListA = result;
        this.loadSecondList();
      },
      (error) => this.failureHandler()
    );
  }

  private loadSecondList(): void {
    this.statusMessage = 'compareItemListsComponent.state.readingProjectB';
    this.articleDataService.getItemListByProjectId(this.projectB.id, this.projectB.currentVersion).subscribe(
      (result) => {
        this.itemListB = result;
        this.buildComparedList();
      },
      (error) => this.failureHandler()
    );
  }

  private buildComparedList(): void {
    this.statusMessage = 'compareItemListsComponent.state.comparingLists';
    let i: number = 0;
    const newComparedList: CompareListElement[] = [];
    while (i < this.itemListA.length) {
      const itemA: ItemListObjectVO = this.itemListA[i];
      let articleNo: string = itemA.articleNumber;
      if (this.projectContentPresenter.showCustomArticleNumber) {
        const mapping: ArticleNumberMappingVO = ArticleNumberMappingUtils.findMappingByArticleNumber(articleNo, this.userModel.articleNumberMappings);
        if (mapping) {
          articleNo = mapping.customArticleNumber;
        }
      }
      const newCompareListElementA: CompareListElement = { amountA: itemA.amount, amountB: 0, articleNumber: articleNo };
      newComparedList.push(newCompareListElementA);
      i++;
    }
    i = 0;
    while (i < this.itemListB.length) {
      const itemB: ItemListObjectVO = this.itemListB[i];
      let articleNo: string = itemB.articleNumber;
      if (this.projectContentPresenter.showCustomArticleNumber) {
        const mapping: ArticleNumberMappingVO = ArticleNumberMappingUtils.findMappingByArticleNumber(articleNo, this.userModel.articleNumberMappings);
        if (mapping) {
          articleNo = mapping.customArticleNumber;
        }
      }
      let j: number = 0;
      let found: boolean = false;
      while (!found && j < newComparedList.length) {
        const compareElement: CompareListElement = newComparedList[j];
        if (compareElement.articleNumber === articleNo) {
          found = true;
          compareElement.amountB = itemB.amount;
        }
        j++;
      }
      if (!found) {
        const newCompareListElementB: CompareListElement = { amountA: 0, amountB: itemB.amount, articleNumber: articleNo };
        newComparedList.push(newCompareListElementB);
      }
      i++;
    }
    this.comparedList = newComparedList.sort((a: CompareListElement, b: CompareListElement) =>
      StringUtils.stringCompare(a.articleNumber, b.articleNumber, true)
    );
    this.statusMessage = '';
  }

  private failureHandler(): void {
    this.statusMessage = 'compareItemListsComponent.state.errorLoading';
  }

  public onCancel(): void {
    this.bsModalRef.hide();
  }

  public saveAsCSV(): void {
    const fileName: string = this.localeResourceManager.getValueByKey('compareItemListsComponent.label.csv_file_name');
    const labelColA: string = this.localeResourceManager.getValueByKey('compareItemListsComponent.label.colA');
    const labelColB: string = this.localeResourceManager.getValueByKey('compareItemListsComponent.label.colB');
    const labelItemNumber: string = this.localeResourceManager.getValueByKey('compareItemListsComponent.label.itemNumber');
    let fileContent: string = '\ufeff'; // TODO: SMAR-95: Currently using BOM (in CSV part list for CSV export we dont use BOM)
    fileContent += CSVUtils.createLineStringFromArray(
      [labelColA + ' = ' + this.projectNameStringA],
      CSVUtils.VALUES_SEPARATOR,
      CSVUtils.LINES_SEPARATOR,
      true
    );

    if (this.projectA.articleNumberLong?.reducedArticleNumberLong) {
      fileContent += CSVUtils.createLineStringFromArray([this.projectA.articleNumberLong.reducedArticleNumberLong], CSVUtils.VALUES_SEPARATOR, CSVUtils.LINES_SEPARATOR, true);
    }

    fileContent += CSVUtils.createLineStringFromArray([this.projectPathStringA], CSVUtils.VALUES_SEPARATOR, CSVUtils.LINES_SEPARATOR, true);
    fileContent += CSVUtils.createLineStringFromArray(['']);
    fileContent += CSVUtils.createLineStringFromArray(
      [labelColB + ' = ' + this.projectNameStringB],
      CSVUtils.VALUES_SEPARATOR,
      CSVUtils.LINES_SEPARATOR,
      true
    );

    if (this.projectB.articleNumberLong?.reducedArticleNumberLong) {
      fileContent += CSVUtils.createLineStringFromArray([this.projectB.articleNumberLong.reducedArticleNumberLong], CSVUtils.VALUES_SEPARATOR, CSVUtils.LINES_SEPARATOR, true);
    }

    fileContent += CSVUtils.createLineStringFromArray([this.projectPathStringB], CSVUtils.VALUES_SEPARATOR, CSVUtils.LINES_SEPARATOR, true);
    fileContent += CSVUtils.createLineStringFromArray(['']);
    fileContent += CSVUtils.createLineStringFromArray(
      [labelItemNumber, labelColA, labelColB],
      CSVUtils.VALUES_SEPARATOR,
      CSVUtils.LINES_SEPARATOR,
      true
    );
    /*if (this.comparedList.length > 0) {
      fileContent += CSVUtils.createContentString(this.comparedList, ['articleNumber', 'amountA', 'amountB'], CSVUtils.VALUES_SEPARATOR, CSVUtils.LINES_SEPARATOR, true);
    }*/
    let i: number = 0;
    while (i < this.comparedList.length) {
      const item: CompareListElement = this.comparedList[i];
      const propertyNames: string[] = ['articleNumber'];
      if (item.amountA > 0) {
        propertyNames.push('amountA');
      } else {
        propertyNames.push('');
      }
      if (item.amountB > 0) {
        propertyNames.push('amountB');
      } else {
        propertyNames.push('');
      }
      fileContent += CSVUtils.createLineStringFromObject(item, propertyNames, CSVUtils.VALUES_SEPARATOR, CSVUtils.LINES_SEPARATOR, true);
      i++;
    }
    FileUtils.saveTextFile(fileName, fileContent, '.csv', false);
  }
}
